'use client';

import { useState, useEffect, useRef } from 'react';
import ItemCard from '@/components/products/itemCard/ItemCard';
import { useTranslation } from "react-i18next";

export default function ProductCarousel({ products, data }) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [itemsPerView, setItemsPerView] = useState(1);
  const intervalRef = useRef(null);

  // Calculate items per view based on screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1200) {
        setItemsPerView(4);
      } else if (width >= 992) {
        setItemsPerView(3);
      } else if (width >= 640) {
        setItemsPerView(2);
      } else {
        setItemsPerView(1);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate valid scroll positions for card-by-card scrolling
  const maxScrollPosition = Math.max(0, products.length - itemsPerView);
  const totalDots = maxScrollPosition + 1;

  // Auto-scroll functionality - scroll one card at a time
  useEffect(() => {
    if (!isHovered && products && products.length > itemsPerView) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          // Scroll one card at a time, loop back to 0 when reaching the maximum scroll position
          return prevIndex >= maxScrollPosition ? 0 : prevIndex + 1;
        });
      }, 3000); // Change slide every 3 seconds
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovered, products, itemsPerView, maxScrollPosition]);

  // Navigation functions - move one card at a time within valid bounds
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => {
      return prevIndex <= 0 ? maxScrollPosition : prevIndex - 1;
    });
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => {
      return prevIndex >= maxScrollPosition ? 0 : prevIndex + 1;
    });
  };

  if (!products || products.length === 0) {
    return null;
  }

  // Calculate the translation for card-by-card scrolling
  const getTransformValue = () => {
    // Each card takes up (100 / products.length)% of the total container width
    // To move one card, we need to shift by that percentage
    const singleCardWidth = 100 / products.length;
    return currentIndex * singleCardWidth;
  };

  return (
    <div className="py-6 lg:py-12 lg:mt-10 w-full">
      {/* Header Section */}
      <div className="flex">
        <div className="max-w-2xl mx-8 lg:mx-24 my-8">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {data ? data.title : t('products_section.title')}
          </h2>
          <p className="mt-4 text-lg leading-8 text-gray-600">
            {data ? data.description : t('products_section.description')}
            <a
              href={data ? data.linkUrl : "/products"}
              className="inline-flex items-center font-medium text-red-800 hover:underline"
            >
              {data ? data.linkText : t('products_section.link')}
              <svg
                className="w-4 h-4 mx-2"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M1 5h12m0 0L9 1m4 4L9 9"
                />
              </svg>
            </a>
          </p>
        </div>
      </div>

      {/* Carousel Container */}
      <div
        className="relative border-t border-gray-200 pt-8"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Navigation Buttons */}
        {products.length > itemsPerView && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow duration-200 opacity-80 hover:opacity-100"
              aria-label="Previous products"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow duration-200 opacity-80 hover:opacity-100"
              aria-label="Next products"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Products Container */}
        <div className="overflow-hidden mx-8 lg:mx-16">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              transform: `translateX(-${getTransformValue()}%)`,
              width: `${(products.length * 100) / itemsPerView}%`
            }}
          >
            {products.map((product) => (
              <div
                key={product.id}
                className="flex-shrink-0 px-2"
                style={{ width: `${100 / products.length}%` }}
              >
                <ItemCard
                  isProduct={true}
                  item={product}
                  view={t('view')}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Dots Indicator - one dot per scroll position */}
        {products.length > itemsPerView && (
          <div className="flex justify-center mt-6 space-x-2">
            {Array.from({ length: totalDots }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                  index === currentIndex
                    ? 'bg-chimia-600'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to position ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}